import { createFileRoute, useParams } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { NotificationDetail } from '@/components/notifications/NotificationDetail'
import { NotificationsInbox } from '@/components/NotificationsInbox'
import { FilterDropdown } from '@/components/notifications/FilterDropdown'
import { useNotificationContext } from '../../_layout'
import { useNotificationQuery, useBulkUpdateNotificationsMutation } from '@/lib/query-options'
import { useNotificationFilters } from '@/hooks/useNotificationFilters'
import { Bell, MoreVertical, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/useToast'

import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';

export const Route = createFileRoute('/_authenticated/_layout/notifications/$notificationId')({
  component: NotificationDetailPage,
  staticData: {
    title: 'Notifications',
    icon: Bell,
  },
});

function NotificationDetailPage() {
  const { notificationId } = useParams({ from: '/_authenticated/_layout/notifications/$notificationId' });
  const notificationContext = useNotificationContext()
  const [activeTab, setActiveTab] = useState<'open' | 'archived'>('open')
  const [searchInput, setSearchInput] = useState('')
  const { toast } = useToast()

  const organizationData = useAtomValue(organizationDataAtom);
  const { data: notificationData, isLoading } = useNotificationQuery({ organization_id: organizationData?.id }, 1, 100)
  const allNotifications = notificationData?.items || []
  const bulkUpdateMutation = useBulkUpdateNotificationsMutation()

  // Use notification filters
  const {
    filters,
    apiFilters,
    activeFilterCount,
    applyFilters,
    setSearchQuery,
  } = useNotificationFilters(organizationData?.id);

  const selectedNotificationItemId = notificationContext?.selectedNotificationItemId ?? ''
  const setSelectedNotificationItemId = notificationContext?.setSelectedNotificationItemId ?? (() => {})

  // Sync URL param to context on mount
  useEffect(() => {
    if (notificationId && setSelectedNotificationItemId) {
      setSelectedNotificationItemId(notificationId);
    }
  }, [notificationId, setSelectedNotificationItemId]);

  // Debounce search input - only update filter after user stops typing for 300ms
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchQuery(searchInput || undefined);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, setSearchQuery]);

  const selectedNotification = selectedNotificationItemId
    ? allNotifications.find(
      (n) => n.id.toString() === selectedNotificationItemId
        && (activeTab === 'archived' ? n.is_archived : !n.is_archived),
    ) || null
    : null

  // Filter handler
  const handleApplyFilters = (filterUpdates: {
    sources?: string[];
    warningLevels?: string[];
    startDate?: string;
    endDate?: string;
  }) => {
    applyFilters(filterUpdates);
  }

  const handleMarkAllAsRead = async () => {
    if (!notificationData?.items) return;

    const unreadIds = notificationData.items.filter((n) => !n.is_viewed).map((n) => n.id);
    if (unreadIds.length === 0) {
      toast({
        title: 'Info',
        description: 'No unread notifications to mark as read',
      });
      return;
    }

    try {
      await bulkUpdateMutation.mutateAsync({
        notificationIds: unreadIds,
        action: 'mark_read',
      });
      toast({
        title: 'Success',
        description: `Marked ${unreadIds.length} notification(s) as read`,
      });
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast({
        title: 'Error',
        description: `Failed to mark all notifications as read: ${errorMessage}`,
        variant: 'destructive',
      });
    }
  }

  // Show loading spinner while context provider is mounting
  if (!notificationContext) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-[#290BD9] animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3">
        </div>
        <div className="flex items-center gap-3">
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as 'open' | 'archived')}
          >
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="open">Open</TabsTrigger>
              <TabsTrigger value="archived">Archived</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Search Bar */}
          <div className="w-64">
            <Input
              type="text"
              placeholder="Search notifications..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-9 text-sm font-medium"
            />
          </div>

          {/* Filters */}
          {organizationData?.id && (
            <FilterDropdown
              organizationId={organizationData.id}
              selectedSources={filters.sources || []}
              selectedWarningLevels={filters.warningLevels || []}
              startDate={filters.start_date}
              endDate={filters.end_date}
              activeFilterCount={activeFilterCount}
              onApplyFilters={handleApplyFilters}
            />
          )}

          {/* Actions Menu (Ellipsis) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleMarkAllAsRead} className="cursor-pointer">
                <Eye className="h-4 w-4 mr-2" />
                Mark all read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Layout: 12-Column Grid */}
      <div className="flex-1 grid grid-cols-12 gap-1">
        {/* Left: Notifications List (25%) */}
        <div className="col-span-3 sticky top-0 self-start">
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            <NotificationsInbox
              selectedItemId={selectedNotificationItemId}
              onSelectItem={setSelectedNotificationItemId}
              activeTab={activeTab}
              searchQuery={filters.searchQuery}
              apiFilters={apiFilters}
              activeFilterCount={activeFilterCount}
            />
          </div>
        </div>

        {/* Right: Notification Detail (75%) */}
        <div className="col-span-9">
          <NotificationDetail notification={selectedNotification} isLoading={isLoading} />
        </div>
      </div>
    </div>
  )
}
